import { HttpClient } from '../api/client'
import type { AIRequest, AIResponse, AIOperation, ApiResponse } from '@bandos/shared'
import { TokenManager } from '../auth/tokenManager'

/**
 * Core AI service for the ideation pipeline.
 * Handles the generic node processing dispatcher and all generation wrappers
 * that flow through the `/ai/process` endpoint.
 */
export class AICoreService {
  /** Process an AI operation for a specific node */
  static async processNode(
    mindmapId: string,
    nodeId: string,
    operation: AIOperation,
    positions?: { x: number; y: number }[],
    existingSuggestions?: string[],
    userFeedback?: string
  ): Promise<ApiResponse<AIResponse>> {
    const headers = await TokenManager.getAuthHeaders()

    if (!headers.Authorization) {
      return {
        data: null,
        error: {
          message: 'Authentication required. Please log in.',
          code: 'AUTH_REQUIRED',
        },
      }
    }

    const requestData: AIRequest = {
      mindmapId,
      nodeId,
      operation,
      positions,
      ...(existingSuggestions && existingSuggestions.length > 0 && { existingSuggestions }),
      ...(userFeedback && userFeedback.trim().length > 0 && { userFeedback: userFeedback.trim() }),
    }

    return HttpClient.post<AIResponse>('/ai/process', requestData, headers)
  }

  /** Classify a phase 3 node as UI_DIGITAL or NON_UI before generating phase 4 ideas */
  static async classifyBranchType(
    mindmapId: string,
    nodeId: string
  ): Promise<ApiResponse<AIResponse>> {
    return AICoreService.processNode(mindmapId, nodeId, 'classify_branch_type')
  }

  /** Generate ideas from a job node */
  static async generateIdeas(
    mindmapId: string,
    nodeId: string,
    positions?: { x: number; y: number }[],
    existingSuggestions?: string[],
    userFeedback?: string
  ): Promise<ApiResponse<AIResponse>> {
    return AICoreService.processNode(
      mindmapId,
      nodeId,
      'generate_idea',
      positions,
      existingSuggestions,
      userFeedback
    )
  }

  /** Generate unmet needs directly from a venture node */
  static async generateNeedsFromVenture(
    mindmapId: string,
    nodeId: string,
    positions?: { x: number; y: number }[],
    existingSuggestions?: string[],
    userFeedback?: string
  ): Promise<ApiResponse<AIResponse>> {
    return AICoreService.processNode(
      mindmapId,
      nodeId,
      'generate_needs_from_venture',
      positions,
      existingSuggestions,
      userFeedback
    )
  }

  /** Synthesize unmet needs from venture-scoped research (surveys, interviews, uploaded docs) */
  static async synthesizeNeedsFromResearch(
    mindmapId: string,
    nodeId: string,
    positions?: { x: number; y: number }[],
    existingSuggestions?: string[],
    userFeedback?: string
  ): Promise<ApiResponse<AIResponse>> {
    return AICoreService.processNode(
      mindmapId,
      nodeId,
      'synthesize_needs_from_research',
      positions,
      existingSuggestions,
      userFeedback
    )
  }
}
